import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Regret, CATEGORY_COLORS, CATEGORY_LABELS } from '../types';
import { likeRegret } from '../api';

interface RegretCardProps {
  regret: Regret;
  showDetails?: boolean;
}

function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  const intervals: [number, string][] = [
    [31536000, 'year'],
    [2592000, 'month'],
    [86400, 'day'],
    [3600, 'hour'],
    [60, 'minute'],
  ];

  for (const [secondsInInterval, label] of intervals) {
    const interval = Math.floor(seconds / secondsInInterval);
    if (interval >= 1) {
      return `${interval} ${label}${interval > 1 ? 's' : ''} ago`;
    }
  }

  return 'just now';
}

export default function RegretCard({ regret, showDetails = false }: RegretCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [likes, setLikes] = useState(regret.likes);
  const [hasLiked, setHasLiked] = useState(false);
  const [isLiking, setIsLiking] = useState(false);

  const bgColor = CATEGORY_COLORS[regret.category_tag];
  const categoryLabel = CATEGORY_LABELS[regret.category_tag];
  const timeAgo = formatTimeAgo(regret.submitted_at);

  const handleLike = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (hasLiked || isLiking) return;

    setIsLiking(true);
    try {
      const result = await likeRegret(regret.id);
      setLikes(result.likes);
      setHasLiked(true);
    } catch {
      // Silently fail - user might have already liked
    } finally {
      setIsLiking(false);
    }
  };

  // Vary card sizes slightly for organic feel
  const sizeVariant = regret.text.length > 120 ? 'p-6' : regret.text.length > 60 ? 'p-5' : 'p-4';

  return (
    <Link
      to={`/r/${regret.id}`}
      className={`card ${bgColor} ${sizeVariant} block relative group cursor-pointer`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Regret text */}
      <p className="font-serif text-gray-800 leading-relaxed text-lg">
        "{regret.text}"
      </p>

      {/* Hover overlay with details */}
      <div
        className={`
          absolute inset-0 ${bgColor} rounded-xl p-4 flex flex-col justify-between
          transition-opacity duration-200
          ${isHovered || showDetails ? 'opacity-100' : 'opacity-0 pointer-events-none'}
        `}
      >
        <p className="font-serif text-gray-800 leading-relaxed text-lg">
          "{regret.text}"
        </p>

        <div className="flex items-center justify-between mt-4 pt-3 border-t border-black/10">
          <div className="flex items-center gap-3 text-sm text-gray-600">
            <span className="px-2 py-1 rounded-full bg-black/5 text-xs font-medium">
              {categoryLabel}
            </span>
            <span>{timeAgo}</span>
          </div>

          <button
            onClick={handleLike}
            disabled={hasLiked || isLiking}
            className={`
              flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium transition-all
              ${hasLiked
                ? 'bg-red-100 text-red-600'
                : 'bg-black/5 text-gray-600 hover:bg-black/10'
              }
            `}
          >
            <svg
              className={`w-4 h-4 ${hasLiked ? 'fill-current' : ''}`}
              fill={hasLiked ? 'currentColor' : 'none'}
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
              />
            </svg>
            {likes}
          </button>
        </div>
      </div>
    </Link>
  );
}
